import './LinearOptimizations.scss';
import cn from 'classnames';
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  MdAllOut,
  MdAttachMoney,
  MdBubbleChart,
  MdNotInterested,
  MdOutlineBugReport,
  MdOutlineFileDownload,
  MdOutlineFormatListNumbered,
  MdOutlineMonetizationOn
} from 'react-icons/md';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@Components/Button';
import { Appearance, Design, Placement, Size, StretchMode } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { Dropdown, MenuItem } from '@Components/Dropdown';
import { Checkbox } from '@Components/FormCheck';
import { NumberFormatter } from '@Components/NumberFormatter/NumberFormatter';
import { Page } from '@Components/Page/Page';
import { FullPageSpinner, Spinner } from '@Components/Spinner/Spinner';
import { BPMTable, CellRenderer, Header, TypedHeader } from '@Components/StickyTable';
import { TableEmptyState } from '@Components/TableEmptyState';
import { useCreativeMap } from '../../redux/creative';
import { useSetError } from '../../redux/modals';
import { exportOptimizationRunSummaryToExcel } from '../../utils/fetch-utils';
import { buildLogsLinks } from './logs-modal-utils';
import { useOptimizations } from './use-linear-optimizations-data';
import { useCreativeInsights } from './use-linear-network-rotations';
import { useConstraintDebuggerData, useRunResult } from './use-linear-run-result';
import { downloadMediaPlan } from './use-linear-run-media-plan';
import {
  AvailLengthPivotRow,
  BuyableSpotRow,
  BuyableTotalsRow,
  CreativePivotRow,
  DaypartPivotRow,
  NetworkPivotRow,
  buildAvailLengthPivot,
  buildBuyableSpotsPivot,
  buildBuyableTotalsRow,
  buildCreativePivot,
  buildDaypartPivot,
  buildNetworkPivot,
  creativeDisplayName,
  nonEmptyNetworkRows
} from './run-view-pivots';
import { CsvRow, calculateTrimmedRows } from './run-view-export';
import {
  LinearOptimizationPageProps,
  Optimization,
  ROUTES,
  RUN_TABS,
  RunTabType
} from './linear-optimizations-constants';
import { ConstraintBindingStatus, formatOptimizationDate } from '../../utils/optimization-utils';
import {
  DOWNLOAD_MENU_OPTIONS,
  DOWNLOAD_OPTION_BUYING_INSTRUCTIONS,
  DOWNLOAD_OPTION_CONSTRAINT_DEBUGGER,
  PAGE_TITLE
} from './run-view-constants';

const formatRunLabel = (run: Optimization): string => {
  const { build_number: buildNumber, date, status } = run;
  return `#${buildNumber} — ${formatOptimizationDate(date)} (${status})`;
};

const renderRunNav = (label: string) => {
  if (label === 'Summary')
    return (
      <span>
        <MdOutlineFormatListNumbered /> {label}
      </span>
    );
  if (label === 'Buyable Spots')
    return (
      <span>
        <MdOutlineMonetizationOn /> {label}
      </span>
    );
  return (
    <span>
      <MdOutlineBugReport /> {label}
    </span>
  );
};

// -----------------------------------------------------------------------------------------------
// Shared totals-row renderers -- a numeric total renders as %/currency by magnitude; a
// buyable-spots total additionally recognizes a "$..." string (see run-view-pivots.ts).
// -----------------------------------------------------------------------------------------------

interface TotalCellProps {
  style?: object;
  classes?: string[];
  children: ReactNode;
}

const TotalCell = ({ style = {}, classes = [], children }: TotalCellProps) => (
  <div style={style} className={cn('pivot-total-cell', classes)}>
    {children}
  </div>
);

const pivotTotalsRenderer: CellRenderer<Element | number | string | undefined> = ({
  data,
  style,
  classes
}) => {
  if (typeof data === 'number' && data <= 1) {
    return (
      <TotalCell style={style} classes={classes}>
        <NumberFormatter value={data} type='%' decimals={1} />
      </TotalCell>
    );
  }
  if (typeof data === 'number') {
    return (
      <TotalCell style={style} classes={classes}>
        <NumberFormatter value={data} type='$' decimals={0} />
      </TotalCell>
    );
  }
  return (
    <TotalCell style={style} classes={classes}>
      {String(data ?? '')}
    </TotalCell>
  );
};

const buyableTotalsRenderer: CellRenderer<Element | number | string | undefined> = ({
  data,
  style,
  classes
}) => {
  if (typeof data === 'number') {
    return (
      <TotalCell style={style} classes={classes}>
        <NumberFormatter value={data} type='#' decimals={0} />
      </TotalCell>
    );
  }
  if (typeof data === 'string' && data.startsWith('$')) {
    return (
      <TotalCell style={style} classes={classes}>
        <NumberFormatter value={parseFloat(data.slice(1))} type='$' decimals={0} />
      </TotalCell>
    );
  }
  return (
    <TotalCell style={style} classes={classes}>
      {String(data ?? '')}
    </TotalCell>
  );
};

// -----------------------------------------------------------------------------------------------
// Export modal
// -----------------------------------------------------------------------------------------------

interface ExportRunModalProps {
  buyableData: BuyableSpotRow[];
  buyableDataByCreativeInsight: Map<string, BuyableSpotRow[]>;
  company: string;
  runId: string;
  onHide: () => void;
}

const ExportRunModal = ({
  buyableData,
  buyableDataByCreativeInsight,
  company,
  runId,
  onHide
}: ExportRunModalProps) => {
  const setError = useSetError();
  const [trimMornings, setTrimMornings] = useState(true);
  const [trimFridays, setTrimFridays] = useState(false);
  const [roundOdd15s, setRoundOdd15s] = useState(false);
  const [roundOddLocals, setRoundOddLocals] = useState(false);
  const [roundOddAll, setRoundOddAll] = useState(false);
  const [creativeInsight, setCreativeInsight] = useState('');
  const [downloading, setDownloading] = useState(false);

  const insightCategories = [...buyableDataByCreativeInsight.keys()];
  const csvRows: CsvRow[] = useMemo(() => {
    const rows = creativeInsight
      ? (buyableDataByCreativeInsight.get(creativeInsight) ?? [])
      : buyableData;
    return rows.length > 0
      ? calculateTrimmedRows(rows, {
          trimMornings,
          trimFridays,
          roundOdd15s,
          roundOddLocals,
          roundOddAll
        })
      : [];
  }, [
    creativeInsight,
    buyableDataByCreativeInsight,
    buyableData,
    trimMornings,
    trimFridays,
    roundOdd15s,
    roundOddLocals,
    roundOddAll
  ]);
  // csvRows[0] is the header; the "spend" column is index 11.
  const totalSpend = csvRows.slice(1).reduce((total, row) => total + Number(row[11] ?? 0), 0);

  return (
    <Modal size='lg' show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Export Run</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Checkbox
          id='export-trim-mornings'
          label='Trim Mornings'
          checked={trimMornings}
          onChange={event => setTrimMornings(event.target.checked)}
        />
        <Checkbox
          id='export-trim-fridays'
          label='Trim Fridays'
          checked={trimFridays}
          onChange={event => setTrimFridays(event.target.checked)}
        />
        <Checkbox
          id='export-round-odd-15s'
          label='Round Up Odd 15s'
          checked={roundOdd15s}
          onChange={event => setRoundOdd15s(event.target.checked)}
        />
        <Checkbox
          id='export-round-odd-locals'
          label='Round Up Odd Locals'
          checked={roundOddLocals}
          onChange={event => setRoundOddLocals(event.target.checked)}
        />
        <Checkbox
          id='export-round-odd-all'
          label='Round Up All Odd Units'
          checked={roundOddAll}
          onChange={event => setRoundOddAll(event.target.checked)}
        />
        {insightCategories.length > 0 && (
          <Form.Group className='export-insight-category'>
            <Form.Label>Creative Insight Category</Form.Label>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              options={insightCategories}
              value={creativeInsight || undefined}
              onChange={setCreativeInsight}
            />
            <Button
              appearance={Appearance.EMPTY}
              size={Size.SM}
              disabled={!creativeInsight}
              onClick={() => setCreativeInsight('')}
            >
              Reset Category
            </Button>
          </Form.Group>
        )}
        <div className='export-total-spend'>
          Total Spend: <NumberFormatter value={totalSpend} type='$' decimals={0} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          leadingElement={<MdOutlineFileDownload />}
          loading={downloading}
          disabled={csvRows.length === 0}
          onClick={async () => {
            setDownloading(true);
            try {
              await exportOptimizationRunSummaryToExcel(csvRows, company, runId);
              onHide();
            } catch (error) {
              setError({
                message: `Failed to export run summary. ${error.message}`,
                reportError: error
              });
            } finally {
              setDownloading(false);
            }
          }}
        >
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// -----------------------------------------------------------------------------------------------
// Constraint Debugger preset filter buttons -- one flat row; "type" presets (icon, brand color)
// and "value" presets (no icon, neutral color) are distinguished by color/icon alone.
// -----------------------------------------------------------------------------------------------

const CONSTRAINT_TYPE_PRESETS = [
  { label: 'Budget', icon: <MdAttachMoney />, token: ['Type', 'is', 'Budget'] },
  { label: 'Spot', icon: <MdBubbleChart />, token: ['Type', 'is', 'Spot'] },
  { label: 'Audience', icon: <MdOutlineFormatListNumbered />, token: ['Type', 'is', 'Audience'] },
  { label: 'Avoid', icon: <MdNotInterested />, token: ['Type', 'is', 'Avoid'] },
  { label: 'Global', icon: <MdAllOut />, token: ['Type', 'is', 'Global'] }
];

const CONSTRAINT_FILTER_PRESETS = [
  {
    label: 'Binding at minimum',
    token: ['Binding', 'is', ConstraintBindingStatus.AT_MINIMUM]
  },
  {
    label: 'Binding at maximum',
    token: ['Binding', 'is', ConstraintBindingStatus.AT_MAXIMUM]
  },
  { label: 'Non-zero', token: ['Final Value', 'is not', '0'] },
  { label: 'Non-zero rounded', token: ['Final Value (Rounded)', 'is not', '0'] },
  {
    label: 'Rounded value violates constraint',
    token: [
      'Constraint Violation Surplus',
      'is not',
      'None',
      'or',
      'Constraint Violation Deficit',
      'is not',
      'None'
    ]
  }
];

const PresetFilters = ({ onSelect }: { onSelect: (token: string[]) => void }) => (
  <div className='preset-filter-bar'>
    {CONSTRAINT_TYPE_PRESETS.map(preset => (
      <Button
        key={preset.label}
        appearance={Appearance.OUTLINED}
        design={Design.BRAND}
        size={Size.SM}
        leadingElement={preset.icon}
        onClick={() => onSelect(preset.token)}
      >
        {preset.label}
      </Button>
    ))}
    {CONSTRAINT_FILTER_PRESETS.map(preset => (
      <Button
        key={preset.label}
        appearance={Appearance.OUTLINED}
        design={Design.NEUTRAL}
        size={Size.SM}
        onClick={() => onSelect(preset.token)}
      >
        {preset.label}
      </Button>
    ))}
  </div>
);

// -----------------------------------------------------------------------------------------------
// RunView
// -----------------------------------------------------------------------------------------------

export const RunView = ({ company }: LinearOptimizationPageProps) => {
  const { kpiWithRunId = '' } = useParams();
  const splitIndex = kpiWithRunId.lastIndexOf('_');
  const kpi = kpiWithRunId.substring(0, splitIndex);
  const runId = kpiWithRunId.substring(splitIndex + 1);
  const navigate = useNavigate();
  const basePath = `/${company}/linear/optimizations`;

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const selectedTab: RunTabType =
    Object.values(RUN_TABS).find(tab => tab === requestedTab) ?? RUN_TABS.SUMMARY;

  const setError = useSetError();
  const { data: optimizations, error: optimizationsError } = useOptimizations(company);
  const { data: runResult, error: runResultError } = useRunResult(kpi, runId);
  const { data: creativeInsights, error: creativeInsightsError } = useCreativeInsights(company);
  const { creativeMap } = useCreativeMap({ company, streaming: false, mediaTypes: ['linear'] });
  const constraintDebugger = useConstraintDebuggerData();
  // Separate mutation instance for the Download menu's "Constraint Debugger Data" option --
  // sharing constraintDebugger would flip the tab's isPending/isSuccess render branches back to a
  // spinner (blanking the already-loaded table) any time the file is downloaded while that tab is
  // open, since it's the same mutation object driving both.
  const constraintDebuggerDownload = useConstraintDebuggerData();

  useEffect(() => {
    const error = optimizationsError ?? runResultError ?? creativeInsightsError;
    if (error) {
      setError({ message: `Failed to load run data: ${error.message}`, reportError: error });
    }
  }, [optimizationsError, runResultError, creativeInsightsError, setError]);

  const [showExportDialog, setShowExportDialog] = useState(false);
  const [buyableFilteredData, setBuyableFilteredData] = useState<BuyableSpotRow[]>([]);
  const [dynamicTokens, setDynamicTokens] = useState<string[]>();
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  // build_number isn't guaranteed unique across KPIs.
  const runInfo = optimizations?.find(
    row => row.company_id === kpi && String(row.build_number) === runId
  );

  // The run-picker Dropdown below switches runs via navigate() on this same route (only
  // kpiWithRunId changes), so RunView never remounts -- without this, constraintDebugger's
  // isSuccess/data from the PREVIOUS run would persist, and the auto-load effect below (gated on
  // isIdle) would never re-fire for the newly selected run.
  useLayoutEffect(() => {
    constraintDebugger.reset();
    // Reset only on an actual run change, not on every constraintDebugger identity change (a new
    // mutation object every render would otherwise reset it right after every mutate() call too).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kpi, runId]);

  // Load the Constraint Debugger's data automatically as soon as that tab is selected (or the
  // page is deep-linked straight into it), rather than gating it behind an explicit "Download
  // constraint data" button click -- downloading a local copy is a separate action now, available
  // from the top-level Download menu instead.
  useEffect(() => {
    if (
      selectedTab === RUN_TABS.CONSTRAINT_DEBUGGER &&
      constraintDebugger.isIdle &&
      runInfo?.branch
    ) {
      constraintDebugger.mutate({
        branch: runInfo.branch,
        kpi: runInfo.company_id,
        runId: runInfo.build_number,
        saveLocally: false
      });
    }
  }, [selectedTab, runInfo, constraintDebugger]);

  const logsLinks = useMemo(
    () =>
      runInfo
        ? buildLogsLinks({
            kpi: runInfo.company_id,
            buildNumber: runInfo.build_number,
            start: runInfo.start,
            end: runInfo.end
          })
        : [],
    [runInfo]
  );
  // Not memoized: DropdownMenu mutates `selected` on these item objects in place on click, so a
  // memoized array would keep the last-clicked link permanently bold across every later open.
  const logsMenuItems: MenuItem<string>[] = logsLinks.map(link => ({
    value: link.href,
    label: link.label
  }));
  const onLogsItemClick = useCallback((item: MenuItem<string>) => {
    window.open(item.value, '_blank', 'noopener,noreferrer');
  }, []);

  const kpiRuns = useMemo(
    () =>
      (optimizations ?? [])
        .filter(row => row.company_id === kpi)
        .sort((a, b) => Number(b.build_number) - Number(a.build_number)),
    [optimizations, kpi]
  );
  const runOptions = useMemo(
    () => kpiRuns.map(run => ({ value: run.build_number, label: formatRunLabel(run) })),
    [kpiRuns]
  );
  const onRunChange = useCallback(
    (newRunId: string) =>
      navigate(
        `${basePath}/${ROUTES.RUNS.uri}/${kpi}_${newRunId}${selectedTab === RUN_TABS.SUMMARY ? '' : `?tab=${selectedTab}`}`
      ),
    [navigate, basePath, kpi, selectedTab]
  );

  const noSpacesCreativeMap = useMemo(() => {
    const entries = Object.values(creativeMap ?? {});
    return Object.fromEntries(
      entries.map(entry => [`ad${entry.modelEntry};${entry.name.replace(/ /g, '')}`, entry.name])
    );
  }, [creativeMap]);

  // ---- Summary tab pivots -------------------------------------------------------------------

  const { rows: networkPivot, totalSpend } = useMemo(
    () => buildNetworkPivot(runResult ?? []),
    [runResult]
  );
  const { rows: daypartPivot, totalsRow: daypartTotalsRow } = useMemo(
    () => buildDaypartPivot(runResult ?? [], totalSpend),
    [runResult, totalSpend]
  );
  const {
    rows: availLengthPivot,
    totalsRow: availLengthTotalsRow,
    showLength
  } = useMemo(() => buildAvailLengthPivot(runResult ?? [], totalSpend), [runResult, totalSpend]);
  const { rows: creativePivot, totalsRow: creativeTotalsRow } = useMemo(
    () => buildCreativePivot(runResult ?? [], totalSpend),
    [runResult, totalSpend]
  );

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await downloadMediaPlan(kpi, runId);
    } catch (error) {
      setError({ message: `Failed to download Excel. ${error.message}`, reportError: error });
    } finally {
      setDownloadingExcel(false);
    }
  };

  const overview: [string, ReactNode][] = runInfo
    ? [
        ['#', runId],
        ['Status', runInfo.status],
        ['Date', formatOptimizationDate(runInfo.date)],
        ['Constraint', runInfo.constraint_name],
        ['OF', <NumberFormatter value={Number(runInfo.objective_value)} type='' decimals={1} />],
        ['Cost', <NumberFormatter value={Number(runInfo.cost)} type='$' decimals={0} />],
        ['Budget', <NumberFormatter value={Number(runInfo.budget)} type='$' decimals={0} />]
      ]
    : [];

  // ---- Buyable Spots tab ---------------------------------------------------------------------

  const { rows: buyableData, creativeColumns } = useMemo(
    () => buildBuyableSpotsPivot(runResult ?? []),
    [runResult]
  );
  const buyableTotalsRow: BuyableTotalsRow | undefined =
    buyableFilteredData.length > 0 ? buildBuyableTotalsRow(buyableFilteredData) : undefined;

  const buyableDataByCreativeInsight = useMemo(() => {
    const result = new Map<string, BuyableSpotRow[]>();
    if (!runResult || !creativeInsights) return result;

    const scopedRows = nonEmptyNetworkRows(runResult);
    for (const category of creativeInsights.insightsCategoriesData) {
      const scoped = scopedRows.filter(row => {
        const creativeName = creativeDisplayName(row);
        return (creativeInsights.creativeInsightsData[creativeName] ?? []).includes(category);
      });
      if (scoped.length > 0) {
        result.set(category, buildBuyableSpotsPivot(scoped).rows);
      }
    }
    return result;
  }, [runResult, creativeInsights]);

  // ---- Header definitions ---------------------------------------------------------------------

  const networkPivotHeaders: TypedHeader<NetworkPivotRow>[] = [
    { label: 'Network', name: 'network', flex: 1, minFlexWidth: 150 },
    { label: 'Avail', name: 'avails' },
    {
      label: 'Spend',
      name: 'spend',
      renderer: row => <NumberFormatter value={row.spend} type='$' decimals={0} />
    },
    {
      label: '% of Spend',
      name: 'pct',
      width: 120,
      renderer: row => <NumberFormatter value={row.pct} type='%' decimals={1} />
    }
  ];

  const daypartPivotHeaders: TypedHeader<DaypartPivotRow>[] = [
    { label: 'Daypart', name: 'daypart', flex: 1, minFlexWidth: 150 },
    {
      label: 'Weekday',
      name: 'weekday',
      renderer: row => <NumberFormatter value={row.weekday} type='%' decimals={1} />
    },
    {
      label: 'Weekend',
      name: 'weekend',
      renderer: row => <NumberFormatter value={row.weekend} type='%' decimals={1} />
    },
    {
      label: 'Grand Total',
      name: 'pct',
      width: 120,
      renderer: row => <NumberFormatter value={row.pct} type='%' decimals={1} />
    }
  ];

  const availLengthPivotHeaders: TypedHeader<AvailLengthPivotRow>[] = [
    { label: 'Avail', name: 'avail', flex: 1, minFlexWidth: 100 },
    ...(showLength['60s']
      ? [
          {
            label: '60s',
            name: 'pct60s',
            renderer: (row: AvailLengthPivotRow) => (
              <NumberFormatter value={row.pct60s ?? 0} type='%' decimals={1} />
            )
          }
        ]
      : []),
    ...(showLength['30s']
      ? [
          {
            label: '30s',
            name: 'pct30s',
            renderer: (row: AvailLengthPivotRow) => (
              <NumberFormatter value={row.pct30s ?? 0} type='%' decimals={1} />
            )
          }
        ]
      : []),
    ...(showLength['15s']
      ? [
          {
            label: '15s',
            name: 'pct15s',
            renderer: (row: AvailLengthPivotRow) => (
              <NumberFormatter value={row.pct15s ?? 0} type='%' decimals={1} />
            )
          }
        ]
      : []),
    {
      label: 'Grand Total',
      name: 'pct',
      width: 120,
      renderer: row => <NumberFormatter value={row.pct} type='%' decimals={1} />
    }
  ];

  const creativePivotHeaders: TypedHeader<CreativePivotRow>[] = [
    { label: 'Creative', name: 'creative', flex: 1, minFlexWidth: 400 },
    {
      label: 'Spend',
      name: 'spend',
      renderer: row => <NumberFormatter value={row.spend} type='$' decimals={0} />
    },
    {
      label: '% of Spend',
      name: 'pct',
      width: 120,
      renderer: row => <NumberFormatter value={row.pct} type='%' decimals={1} />
    }
  ];

  const buyableHeaders: TypedHeader<BuyableSpotRow>[] = [
    { label: 'Network', name: 'network', flex: 1, minFlexWidth: 100 },
    { label: 'Avail', name: 'avail' },
    { label: 'Length', name: 'spotLength' },
    { label: 'Day', name: 'day' },
    { label: 'Daypart', name: 'daypart', width: 150 },
    { label: 'Rotation Name', name: 'daypartName', width: 150 },
    { label: 'Secured', name: 'secured' },
    { label: 'Spots', name: 'totalSpots' },
    {
      label: 'Spot Rate',
      name: 'cost',
      renderer: row => <NumberFormatter value={row.cost} type='$' decimals={0} />
    },
    {
      label: 'Spend',
      name: 'totalSpend',
      renderer: row => <NumberFormatter value={row.totalSpend} type='$' decimals={0} />
    },
    ...creativeColumns.map(creative => ({ label: creative, name: creative }))
  ];

  // BPMTable's header row passes each header's plain label string as `data` (topData is built as
  // `headers.map(h => h.label ?? '')`), not a Header object -- headersRenderer's CellRenderer<Header>
  // type is inaccurate for header cells specifically; String(data) is the same workaround
  // MarketingInputs.tsx's own headersRenderer uses for this exact BPMTable quirk.
  const buyableHeadersRenderer: CellRenderer<Header> = ({ data, style = {}, classes = [] }) => {
    const label = String(data ?? '');
    const displayLabel = noSpacesCreativeMap[label] ?? label;
    return (
      <div style={style} className={cn('buyable-header-cell', classes)}>
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip id={`buyable-header-${label}`}>{label}</Tooltip>}
        >
          <span className='buyable-header-label'>{displayLabel}</span>
        </OverlayTrigger>
      </div>
    );
  };

  const constraintsHeaders: TypedHeader<Record<string, unknown>>[] = [
    { label: 'Type', name: 'constraintType', width: 150 },
    { label: 'Name', name: 'Name', flex: 1, minFlexWidth: 200 },
    { label: 'Min Constraint', name: 'MinConstraint', width: 150 },
    { label: 'Max Constraint', name: 'MaxConstraint', width: 150 },
    { label: 'Final Value', name: 'FinalValue', flex: 1, minFlexWidth: 200 },
    { label: 'Final Value (Rounded)', name: 'FinalValueNonFractional', width: 150 },
    { label: 'Binding', name: 'isBinding', width: 150 },
    { label: 'Constraint Violation Surplus', name: 'NonFractionalSurplus', width: 150 },
    { label: 'Constraint Violation Deficit', name: 'NonFractionalDeficit', width: 150 }
  ];

  const navs = [
    { key: RUN_TABS.SUMMARY, label: 'Summary' },
    { key: RUN_TABS.BUYABLE_SPOTS, label: 'Buyable Spots' },
    { key: RUN_TABS.CONSTRAINT_DEBUGGER, label: 'Constraint Debugger' }
  ];

  return (
    <Page
      title={
        <span className='linear-optimization-run-title'>
          <Link className='optimization-title-link' to={basePath}>
            {PAGE_TITLE}
          </Link>
          <span aria-hidden='true'>/</span>
          {runOptions.length > 0 ? (
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              stretch={StretchMode.FIT}
              size={Size.SM}
              className='optimization-run-picker'
              value={runId}
              valueContent={<span>Run #{runId}</span>}
              options={runOptions}
              onChange={onRunChange}
            />
          ) : (
            <span>Run #{runId}</span>
          )}
        </span>
      }
      pageType={PAGE_TITLE}
      minWidth='1120px'
      minHeight='600px'
      navs={navs}
      selectedNav={selectedTab}
      navRenderer={renderRunNav}
      onNav={tab => setSearchParams(tab === RUN_TABS.SUMMARY ? {} : { tab })}
      actions={
        <div className='linear-run-view-actions'>
          <Dropdown
            overlayPlacement={Placement.RIGHT}
            size={Size.MD}
            stretch={StretchMode.FIT}
            disabled={!runInfo}
            label='View Logs'
            leadingIcon={<MdOutlineBugReport />}
            menuAlign='end'
            menuItems={logsMenuItems}
            onChange={() => {}}
            itemOnClick={onLogsItemClick}
          />
          <DownloadDropdown
            appearance={Appearance.FILLED}
            design={Design.BRAND}
            size={Size.MD}
            disabled={!runInfo || downloadingExcel}
            menuOptions={DOWNLOAD_MENU_OPTIONS}
            disabledMenuOptions={{
              // The Export modal's Creative Insight Category section only renders once
              // creativeInsights resolves; opening earlier makes it pop in under the user.
              [DOWNLOAD_OPTION_BUYING_INSTRUCTIONS]: !creativeInsights,
              [DOWNLOAD_OPTION_CONSTRAINT_DEBUGGER]: !runInfo?.branch
            }}
            onClickOptions={[
              handleDownloadExcel,
              () => setShowExportDialog(true),
              () =>
                runInfo?.branch &&
                constraintDebuggerDownload.mutate({
                  branch: runInfo.branch,
                  kpi: runInfo.company_id,
                  runId: runInfo.build_number,
                  saveLocally: true
                })
            ]}
          />
        </div>
      }
    >
      <div className='linear-run-view'>
        {selectedTab === RUN_TABS.SUMMARY &&
          (runResult ? (
            <div className='result-pivot'>
              <div className='result-pivot-column left'>
                <section className='result-pivot-panel' aria-label='Network allocation'>
                  <BPMTable
                    headerHeight={40}
                    headers={networkPivotHeaders}
                    data={networkPivot}
                    filterBar={false}
                    totals={{ network: 'Total', spend: totalSpend, pct: 1 }}
                    totalsRenderer={pivotTotalsRenderer}
                  />
                </section>
              </div>
              <div className='result-pivot-column middle'>
                <section className='result-pivot-panel' aria-label='Daypart allocation'>
                  <BPMTable
                    headerHeight={40}
                    headers={daypartPivotHeaders}
                    data={daypartPivot}
                    filterBar={false}
                    totals={daypartTotalsRow}
                    totalsRenderer={pivotTotalsRenderer}
                  />
                </section>
                <section className='result-pivot-panel' aria-label='Avail length allocation'>
                  <BPMTable
                    headerHeight={40}
                    headers={availLengthPivotHeaders}
                    data={availLengthPivot}
                    filterBar={false}
                    totals={availLengthTotalsRow}
                    totalsRenderer={pivotTotalsRenderer}
                  />
                </section>
                <section className='result-pivot-panel' aria-label='Creative allocation'>
                  <BPMTable
                    headerHeight={40}
                    headers={creativePivotHeaders}
                    data={creativePivot}
                    filterBar={false}
                    totals={creativeTotalsRow}
                    totalsRenderer={pivotTotalsRenderer}
                  />
                </section>
              </div>
              <div className='result-pivot-column right'>
                <section className='result-pivot-panel overview-panel' aria-label='Run details'>
                  <table
                    className='optimization-summary-table'
                    aria-label='Optimization run details'
                  >
                    <tbody>
                      {overview.map(([key, value]) => (
                        <tr key={key}>
                          <th>{key}</th>
                          <td>{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              </div>
            </div>
          ) : (
            <FullPageSpinner />
          ))}

        {selectedTab === RUN_TABS.BUYABLE_SPOTS &&
          (runResult ? (
            <div className='result-buyable'>
              <BPMTable
                headerHeight={40}
                // Shares a route with the Constraint Debugger tab's table below.
                storageKeySuffix='buyable-spots'
                headersRenderer={buyableHeadersRenderer}
                headers={buyableHeaders}
                data={buyableData}
                onFilteredDataChange={setBuyableFilteredData}
                // buyableTotalsRow.totalSpend is deliberately a "$..." string (see run-view-pivots.ts)
                // so buyableTotalsRenderer can detect and reformat it -- a real shape mismatch against
                // BuyableSpotRow.totalSpend's number, not a type error to paper over.
                totals={buyableTotalsRow as unknown as Partial<BuyableSpotRow> | undefined}
                totalsRenderer={buyableTotalsRenderer}
              />
            </div>
          ) : (
            <FullPageSpinner />
          ))}

        {selectedTab === RUN_TABS.CONSTRAINT_DEBUGGER && (
          <div className='result-constraints'>
            {!runInfo && <Spinner size={50} />}
            {runInfo && !runInfo.branch && (
              <TableEmptyState message="Constraint debugging isn't available for this run -- no branch is on record for it." />
            )}
            {runInfo?.branch && (constraintDebugger.isIdle || constraintDebugger.isPending) && (
              <Spinner size={50} />
            )}
            {constraintDebugger.isError && (
              <div className='constraint-debugger-error'>
                <TableEmptyState message="Couldn't load constraint data for this run." />
                {
                  // Retrying is just a reset -- the auto-load effect above re-fires as soon as
                  // it sees isIdle again, so there's no need to duplicate its mutate() call here.
                }
                <Button
                  size={Size.LG}
                  appearance={Appearance.OUTLINED}
                  onClick={() => constraintDebugger.reset()}
                >
                  Retry
                </Button>
              </div>
            )}
            {constraintDebugger.isSuccess && (
              <>
                <PresetFilters onSelect={setDynamicTokens} />
                <BPMTable
                  headerHeight={40}
                  storageKeySuffix='constraint-debugger'
                  defaultAdvancedFilter
                  dynamicTokens={dynamicTokens}
                  headers={constraintsHeaders}
                  data={constraintDebugger.data}
                />
              </>
            )}
          </div>
        )}
      </div>

      {showExportDialog && (
        <ExportRunModal
          buyableData={buyableData}
          buyableDataByCreativeInsight={buyableDataByCreativeInsight}
          company={company}
          runId={runId}
          onHide={() => setShowExportDialog(false)}
        />
      )}
    </Page>
  );
};
